// Decoders for NASR raw codes that appear on airport detail.
//
// Most NASR fields ship as terse codes ("Y-L", "WGY", "WG") to fit
// a fixed-width row. These helpers map them to the labels a pilot
// would actually want to read on the Info tab. Keep them small, pure,
// and easy to extend as we encounter new codes.

const OWNERSHIP: Record<string, string> = {
  PU: 'Public',
  PR: 'Private',
  MA: 'Air Force',
  MN: 'Navy',
  MR: 'Army',
  CG: 'Coast Guard',
};

const FACILITY_USE: Record<string, string> = {
  PU: 'Public use',
  PR: 'Private use',
};

const WIND_INDICATOR: Record<string, string> = {
  N: 'None',
  Y: 'Yes (unlighted)',
  'Y-L': 'Yes (lighted)',
};

const SEGMENTED_CIRCLE: Record<string, string> = {
  Y: 'Yes',
  N: 'None',
  'Y-L': 'Yes (lighted)',
};

const BEACON_COLOR: Record<string, string> = {
  WG:  'White-Green (lighted land)',
  WY:  'White-Yellow (lighted seaplane)',
  WGY: 'White-Green-Yellow (heliport)',
  SWG: 'Split White-Green (lighted military)',
  W:   'White (unlighted land)',
  Y:   'Yellow (unlighted seaplane)',
  G:   'Green (lighted land)',
  N:   'None',
};

const TRANSIENT_STORAGE: Record<string, string> = {
  BUOY: 'Buoy',
  HGR:  'Hangar',
  TIE:  'Tie-down',
};

const OTHER_SERVICES: Record<string, string> = {
  AFRT:  'Air freight',
  AGRI:  'Crop dusting',
  AMB:   'Air ambulance',
  AVNCS: 'Avionics',
  BCHGR: 'Beaching gear',
  CARGO: 'Cargo handling',
  CHTR:  'Charter service',
  GLD:   'Glider service',
  INSTR: 'Pilot instruction',
  PAJA:  'Parachute jump activity',
  RNTL:  'Aircraft rental',
  SALES: 'Aircraft sales',
  SURV:  'Annual surveying',
  TOW:   'Glider towing',
};

// FAA NPIAS / federal-agreements code letters.
const NPIAS_CODES: Record<string, string> = {
  N: 'NPIAS',
  B: 'Privately-owned navaid installation (F&E)',
  G: 'Grant agreements (FAAP/ADAP/AIP)',
  H: 'Handicap accessibility compliance',
  P: 'Surplus property (PL 289)',
  R: 'Surplus property (Reg 16-WAA)',
  S: 'Section 16 conveyance',
  V: 'Advance planning agreement (FAAP)',
  X: 'Obligations assumed by transfer',
  Y: 'Title VI Civil Rights assurances',
  Z: 'Section 303(c) conveyance',
  '1': 'Expired grant (still effective, public use)',
  '2': '303(c) expired (still effective, public use)',
  '3': 'AP-4 / DLAND / DCLA expired',
};

export function decodeOwnership(code: string | undefined | null): string | null {
  if (!code) return null;
  return OWNERSHIP[code] ?? code;
}
export function decodeFacilityUse(code: string | undefined | null): string | null {
  if (!code) return null;
  return FACILITY_USE[code] ?? code;
}
export function decodeWindIndicator(code: string | undefined | null): string | null {
  if (!code) return null;
  return WIND_INDICATOR[code] ?? code;
}
export function decodeSegmentedCircle(code: string | undefined | null): string | null {
  if (!code) return null;
  return SEGMENTED_CIRCLE[code] ?? code;
}
export function decodeBeaconColor(code: string | undefined | null): string | null {
  if (!code) return null;
  return BEACON_COLOR[code] ?? code;
}
export function decodeTransientStorage(codes: string[] | undefined): string[] {
  if (!codes) return [];
  return codes.map((c) => TRANSIENT_STORAGE[c] ?? c);
}
export function decodeOtherServices(codes: string[] | undefined): string[] {
  if (!codes) return [];
  return codes.map((c) => OTHER_SERVICES[c] ?? c);
}
export function decodeNpiasCodes(codes: string | undefined | null): string[] {
  if (!codes) return [];
  const out: string[] = [];
  for (const ch of codes) {
    const label = NPIAS_CODES[ch];
    if (label) out.push(label);
  }
  return out;
}
/**
 * Decode the lighting schedule string. NASR publishes:
 *   "SS-SR"    → Sunset to sunrise
 *   "DUSK-DAWN"→ same idea, occasional encoding
 *   "SEE RMK"  → details live in an RMK record
 *   blank      → not published
 *   anything else → pass through (specific hours like "0700-2300")
 */
export function decodeLightingSchedule(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed === 'SS-SR' || trimmed === 'DUSK-DAWN') return 'Sunset–sunrise';
  if (trimmed === 'SEE RMK') return 'See remarks';
  return trimmed;
}

/**
 * The MM/YYYY activation date format. Returns the same string when
 * already in the expected shape, otherwise null.
 */
export function decodeActivationDate(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  return null;
}

/** Pretty-print E.164-ish NASR phone numbers (often "(XXX) XXX-XXXX"). */
export function decodePhone(phone: string | undefined | null): string | null {
  if (!phone) return null;
  return phone.trim() || null;
}

/**
 * A few common RMK element-name prefixes have meaningful UX labels.
 * Anything else falls through to the raw key.
 */
export const RMK_LABEL_MAP: Record<string, string> = {
  E70:    'Operations note',
  E147:   'Traffic pattern altitude',
  A81:    'Lighting',
  'A81-APT': 'Airport lighting',
  'A81-BCN': 'Beacon lighting',
  A70:    'Fuel',
  A75:    'Storage',
  A76:    'Services',
  A85:    'Tower',
  E80:    'Customs',
  E155:   'Inspection',
};
export function decodeRmkLabel(key: string): string {
  if (RMK_LABEL_MAP[key]) return RMK_LABEL_MAP[key];
  // RMK keys often start with the elem id then "-RUNWAY" suffix; strip
  // sequential A110-N "general remark" tags down to "General".
  if (/^A110-\d+$/.test(key)) return 'General';
  // Element-specific (A55-H1) → just show the prefix label.
  const prefix = key.split('-')[0];
  return RMK_LABEL_MAP[prefix] ?? key;
}
